/**
 * 알림 API
 *
 * @CODE:API-201 | SPEC: olulo-mx-admin/.claude/specs/SPEC-201-notification-api.md
 */
import { apiClient } from './client';
import type {
  ApiNotification,
  NotificationListResponse,
  NotificationResponse,
  UnreadCountResponse,
  NotificationType,
} from './types';

export interface GetNotificationsParams {
  type?: 'order_status' | 'promotion' | 'system';
  unread?: boolean;
  page?: number;
  per_page?: number;
}

export const notificationApi = {
  /**
   * 알림 목록 조회
   * GET /api/v1/notifications
   */
  getNotifications: async (
    params?: GetNotificationsParams,
    signal?: AbortSignal,
  ): Promise<NotificationListResponse> => {
    const response = await apiClient.get<NotificationListResponse>(
      '/api/v1/notifications',
      { params: params as Record<string, string | number | boolean | undefined>, signal }
    );
    return response;
  },

  /**
   * 읽지 않은 알림 수 조회
   * GET /api/v1/notifications/unread-count
   */
  getUnreadCount: async (signal?: AbortSignal): Promise<UnreadCountResponse> => {
    const response = await apiClient.get<UnreadCountResponse>(
      '/api/v1/notifications/unread-count',
      { signal },
    );
    return response;
  },

  /**
   * 개별 알림 읽음 처리
   * PUT /api/v1/notifications/{id}/read
   */
  markAsRead: async (notificationId: string): Promise<NotificationResponse> => {
    const response = await apiClient.put<NotificationResponse>(
      `/api/v1/notifications/${notificationId}/read`
    );
    return response;
  },

  /**
   * 전체 알림 읽음 처리
   * PUT /api/v1/notifications/read-all
   */
  markAllAsRead: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      '/api/v1/notifications/read-all'
    );
    return response;
  },

  /**
   * 알림 삭제
   * DELETE /api/v1/notifications/{id}
   */
  deleteNotification: async (
    notificationId: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete<{
      success: boolean;
      message: string;
    }>(`/api/v1/notifications/${notificationId}`);
    return response;
  },
};

/**
 * API 알림 → UI 알림 변환
 */
export function transformApiNotification(apiNotification: ApiNotification): {
  id: string;
  type: 'order' | 'promotion' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  restaurantName?: string;
  orderId?: string;
  storeId?: string;
  promotionId?: string;
  announcementId?: string;
} {
  return {
    id: apiNotification.id,
    type:
      apiNotification.notification_type === 'order_status'
        ? 'order'
        : apiNotification.notification_type,
    title: apiNotification.title,
    message: apiNotification.body,
    timestamp: new Date(apiNotification.created_at),
    isRead: apiNotification.read_at !== null,
    restaurantName: apiNotification.data.store_name,
    // ID 매핑: snake_case → camelCase (숫자를 문자열로 변환)
    orderId: apiNotification.data.order_id ? String(apiNotification.data.order_id) : undefined,
    storeId: apiNotification.data.store_id ? String(apiNotification.data.store_id) : undefined,
    promotionId: apiNotification.data.promotion_id ? String(apiNotification.data.promotion_id) : undefined,
    announcementId: apiNotification.data.announcement_id ? String(apiNotification.data.announcement_id) : undefined,
  };
}

export function transformApiNotifications(
  apiNotifications: ApiNotification[]
): ReturnType<typeof transformApiNotification>[] {
  return apiNotifications.map(transformApiNotification);
}

/**
 * Laravel 브로드캐스트 알림 페이로드 (toBroadcast = toArray flat + Laravel 주입 id/type)
 *
 * API 응답(ApiNotification)과 달리 data 중첩/read_at/created_at 이 없다.
 */
export interface BroadcastNotificationPayload {
  id: string;
  type?: string; // Laravel 이 주입하는 알림 클래스명 (FQCN)
  notification_type: NotificationType;
  title: string;
  body: string;
  order_id?: number;
  order_number?: string;
  status?: string;
  store_name?: string;
  store_id?: number;
  promotion_id?: number;
  announcement_id?: number;
  action_url?: string;
}

/** 브로드캐스트 페이로드 정규화 — 실시간 수신은 항상 미읽음, 수신 시각을 타임스탬프로 사용 */
export function transformBroadcastNotification(payload: BroadcastNotificationPayload): ReturnType<typeof transformApiNotification> {
  return {
    id: payload.id,
    type: payload.notification_type === 'order_status' ? ('order' as const) : payload.notification_type,
    title: payload.title,
    message: payload.body,
    timestamp: new Date(),
    isRead: false,
    restaurantName: payload.store_name,
    orderId: payload.order_id ? String(payload.order_id) : undefined,
    storeId: payload.store_id ? String(payload.store_id) : undefined,
    promotionId: payload.promotion_id ? String(payload.promotion_id) : undefined,
    announcementId: payload.announcement_id ? String(payload.announcement_id) : undefined,
  };
}
